import { useState, useCallback, useEffect, useRef, createContext, useContext, type ReactNode } from 'react';

interface ToastItem { id: string; message: string; type: 'success' | 'error' | 'info' }

interface ToastContextValue {
  toast: (message: string, type?: ToastItem['type']) => void;
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} });

export function useToast() { return useContext(ToastContext); }

const DURATION_MS = 5000;

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stop = useCallback(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
  }, []);
  const start = useCallback(() => {
    stop();
    timer.current = setTimeout(onDismiss, DURATION_MS);
  }, [onDismiss, stop]);

  useEffect(() => { start(); return stop; }, [start, stop]);

  return (
    <div
      className={`toast toast-${item.type}`}
      // Errors are announced assertively; success/info politely.
      role={item.type === 'error' ? 'alert' : 'status'}
      // Pause auto-dismiss while hovered/focused so slow readers don't lose it (WCAG 2.2.1).
      onMouseEnter={stop}
      onMouseLeave={start}
      onFocus={stop}
      onBlur={start}
    >
      <span className="toast-message">{item.message}</span>
      <button type="button" className="toast-dismiss" aria-label="Dismiss" onClick={onDismiss}>×</button>
    </div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, type: ToastItem['type'] = 'info') => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* The container is ALWAYS mounted so screen-reader live regions exist before
          content is injected — otherwise the first toast can be missed (MYSKUX-084). */}
      <div className="toast-container">
        {toasts.map((t) => (
          <ToastView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
